import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Plus } from "lucide-react";
import { toast } from "sonner";
import { PageWrapper } from "@/components/PageWrapper";
import { Button, Card, Skeleton } from "@/components/ui";
import { DesignPackageServices } from "@/services/bedrock/DesignPackageServices";
import {
  ContextType,
  DesignPackageDetail,
  DesignPackageListParams,
  DesignPackageSummary,
} from "@/services/bedrock/dtos/DesignPackage";
import { Page } from "@/services/bedrock/dtos/Page";
import { designKeys } from "@/services/bedrock/queryKeys";
import { designEditorUrl, URLS } from "@/utils/routes";
import { useDynamicPromptsAccess } from "@/utils/useDynamicPromptsAccess";
import { useLocalStorage } from "@/utils/useLocalStorage";
import { AdminDesignStudioHeader } from "@/containers/Admin/DesignStudio/AdminDesignStudioHeader";
import { DesignStudioHelpDialog } from "./help/DesignStudioHelpDialog";
import { CoachDesignStudioHeader } from "./components/CoachDesignStudioHeader";
import { DesignStudioScopeContext } from "./DesignStudioScope";
import { useDesignPackagesInfinite, DesignPackagesInfiniteParams } from "./hooks/useDesignPackagesInfinite";
import { useInFlightPackageChannels } from "./hooks/useInFlightPackageChannels";
import { useInfiniteSentinel } from "./hooks/useInfiniteSentinel";
import { INSPIRATION_HIDDEN_KEY, InspirationStrip, useInspirationPackages } from "./components/InspirationStrip";
import { cardVariantFor, PackageCard, SKELETON_PACKAGE } from "./components/PackageCard";
import { PackageEmptyState } from "./components/PackageEmptyState";
import { PackageListToolbar, PACKAGE_STATUS_FILTERS, PackageStatusFilter } from "./components/PackageListToolbar";
import { TemplateGalleryDialog } from "./components/TemplateGalleryDialog";
import { TemplateQuickPreview } from "./components/TemplateQuickPreview";

const CONTEXT_TYPES: ContextType[] = ["review", "player", "none"];
const LOADING_CARD_COUNT = 8;
const FETCHING_ROW_COUNT = 3;
const STRIP_SKELETON_COUNT = 6;

export interface ListSearchParams {
  q: string;
  type: ContextType | null;
  status: PackageStatusFilter;
  preview: number | null;
  gallery: boolean;
}

export function readListSearchParams(search: string): ListSearchParams {
  const params = new URLSearchParams(search);
  const type = params.get("type") as ContextType | null;
  const status = params.get("status") as PackageStatusFilter | null;
  const preview = Number(params.get("preview"));
  return {
    q: params.get("q")?.trim() ?? "",
    type: type && CONTEXT_TYPES.includes(type) ? type : null,
    status: status && PACKAGE_STATUS_FILTERS.includes(status) ? status : "all",
    preview: Number.isInteger(preview) && preview > 0 ? preview : null,
    gallery: params.get("gallery") === "1",
  };
}

/** `archived` is its own list flag; the other filters are `status` values, and `all` sends none. */
export function statusListParams(status: PackageStatusFilter): Pick<DesignPackageListParams, "status" | "archived"> {
  if (status === "all") return {};
  if (status === "archived") return { archived: true };
  return { status };
}

export function gridListParams(
  filters: Pick<ListSearchParams, "q" | "type" | "status">,
  scope: { scope: "global" | "coach"; coachId: number | null } | undefined
): DesignPackagesInfiniteParams {
  const base: DesignPackagesInfiniteParams = !scope
    ? { scope: "mine" }
    : scope.scope === "global"
    ? { scope: "global" }
    : { scope: "coach", ...(scope.coachId != null ? { coachId: scope.coachId } : {}) };
  return {
    ...base,
    ...(filters.type ? { contextType: filters.type } : {}),
    ...statusListParams(filters.status),
    ...(filters.q ? { q: filters.q } : {}),
    sort: "updatedAt",
    order: "desc",
  };
}

/** Looks through every cached package list (strip, gallery, grid pages) for a package. */
function findCachedPackage(queryClient: QueryClient, id: number): DesignPackageSummary | undefined {
  const cached = queryClient.getQueriesData<Page<DesignPackageSummary> | { pages: Page<DesignPackageSummary>[] }>({
    queryKey: designKeys.all,
  });
  for (const [, data] of cached) {
    if (!data) continue;
    const pages = "pages" in data ? data.pages : [data];
    for (const page of pages) {
      const match = page?.content?.find(pkg => pkg.id === id);
      if (match) return match;
    }
  }
  return undefined;
}

const NewDesignTile: React.FC<{ label: string; onClick(): void }> = ({ label, onClick }) => (
  <Card
    variant="dashed"
    role="button"
    tabIndex={0}
    data-testid="new-design-tile"
    onClick={onClick}
    onKeyDown={event => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    }}
    className="rounded-2xl min-h-60 flex flex-col items-center justify-center gap-2 cursor-pointer text-slate-500 hover:text-slate-700 bg-transparent"
  >
    <Plus className="size-6" aria-hidden="true" />
    <span className="text-sm font-semibold">{label}</span>
  </Card>
);

const StripSkeleton: React.FC = () => (
  <div className="flex gap-3 overflow-hidden" data-testid="inspiration-strip-skeleton">
    {Array.from({ length: STRIP_SKELETON_COUNT }, (_, i) => (
      <Skeleton key={i} className="h-60 w-[200px] shrink-0 rounded-2xl" />
    ))}
  </div>
);

const GRID_CLASS = "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4";

export function DesignStudioListPage(): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const adminScope = React.useContext(DesignStudioScopeContext);
  const isGlobalScope = adminScope?.scope === "global";
  const { canEdit } = useDynamicPromptsAccess();

  const searchFilters = readListSearchParams(location.search);
  // Archived is only offered to editors outside admin Global scope, so a URL can't select it otherwise.
  const archivedAllowed = canEdit && !isGlobalScope;
  const filters: ListSearchParams =
    searchFilters.status === "archived" && !archivedAllowed ? { ...searchFilters, status: "all" } : searchFilters;
  const hasFilters = !!filters.q || !!filters.type || filters.status !== "all";
  const [helpOpen, setHelpOpen] = React.useState(false);
  const [inspirationHidden, setInspirationHidden] = useLocalStorage(INSPIRATION_HIDDEN_KEY, false);

  const updateSearch = React.useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const params = new URLSearchParams(location.search);
      mutate(params);
      const search = params.toString();
      history.replace({ ...location, search: search ? `?${search}` : "" });
    },
    [history, location]
  );

  const setParam = (key: string, value: string | null) =>
    updateSearch(params => (value ? params.set(key, value) : params.delete(key)));

  const gridParams = gridListParams(filters, adminScope);
  const needsCoach = adminScope?.scope === "coach" && adminScope.coachId == null;
  const grid = useDesignPackagesInfinite(gridParams, { enabled: !needsCoach });
  const packages = grid.packages;
  useInFlightPackageChannels(packages);

  const firstRun = !hasFilters && !grid.isLoading && !grid.isError && packages.length === 0 && !needsCoach;
  const stripAllowed = !adminScope;
  const strip = useInspirationPackages(stripAllowed && (!inspirationHidden || firstRun));
  const showStrip = stripAllowed && !inspirationHidden && !firstRun;

  const sentinelRef = useInfiniteSentinel({
    enabled: !!grid.hasNextPage && !grid.isFetchingNextPage,
    onIntersect: () => grid.fetchNextPage(),
  });

  const openNewDesign = () => history.push(adminScope ? URLS.admin.adminNewDesign : URLS.coach.newDesign);
  const openPackage = (pkg: DesignPackageSummary) => history.push(designEditorUrl(pkg.id, { admin: !!adminScope }));

  const previewPkg = React.useMemo(
    () => (filters.preview != null ? findCachedPackage(queryClient, filters.preview) : undefined),
    // Strip and grid data are listed so a reload resolves the package once its list arrives.
    [filters.preview, queryClient, strip.data, grid.data]
  );
  const previewDetail = useQuery({
    queryKey: designKeys.detail(filters.preview ?? 0),
    queryFn: () => DesignPackageServices.get(filters.preview as number),
    // Only a package that isn't in any loaded list (a gallery page, say) needs its own fetch.
    enabled: !adminScope && filters.preview != null && !previewPkg && !strip.isLoading && !grid.isLoading,
  });
  const previewTarget = previewPkg ?? previewDetail.data ?? null;

  const onAdded = (added: DesignPackageDetail) => {
    toast.success("Added as a draft. Refine anything you like, then publish.");
    setParam("preview", null);
    history.push(designEditorUrl(added.id), { justDuplicated: true });
  };

  const onScopeChange = (scope: "global" | "coach") => {
    adminScope?.setScope(scope);
    updateSearch(params => {
      params.delete("q");
      params.delete("status");
    });
  };

  const clearFilters = () =>
    updateSearch(params => {
      params.delete("q");
      params.delete("type");
      params.delete("status");
    });

  const newTileLabel = isGlobalScope ? "New global template" : "New design";
  const newTile = canEdit ? <NewDesignTile label={newTileLabel} onClick={openNewDesign} /> : null;

  const renderGrid = () => {
    if (needsCoach)
      return <p className="text-sm text-slate-500 text-center py-10">Pick a coach to see their designs.</p>;

    if (grid.isLoading) {
      return (
        <div className={GRID_CLASS} data-testid="package-grid">
          {newTile}
          {Array.from({ length: LOADING_CARD_COUNT }, (_, i) => (
            <PackageCard key={i} pkg={SKELETON_PACKAGE} variant="draft" onClick={() => {}} dataSkeleton />
          ))}
        </div>
      );
    }

    if (grid.isError && packages.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
          <AlertCircle className="h-8 w-8 text-slate-300 mb-3" aria-hidden="true" />
          <h3 className="text-lg font-semibold tracking-tight mb-4">We couldn't load your designs.</h3>
          <Button variant="outline" size="sm" onClick={() => grid.refetch()}>
            Retry
          </Button>
        </div>
      );
    }

    if (packages.length === 0) {
      if (hasFilters) {
        return (
          <div className="flex flex-col items-center justify-center py-10 px-4 text-center gap-2">
            <p className="text-sm text-slate-500">No designs match.</p>
            <Button variant="link" onClick={clearFilters}>
              Clear filters
            </Button>
          </div>
        );
      }
      if (isGlobalScope) {
        return (
          <div className="flex flex-col items-center justify-center py-10 px-4 text-center gap-4">
            <p className="text-lg font-semibold text-slate-700">No global templates yet.</p>
            {canEdit && <Button onClick={openNewDesign}>Create the first global template</Button>}
          </div>
        );
      }
      return (
        <PackageEmptyState
          onNewDesign={openNewDesign}
          canEdit={canEdit}
          onPreviewTemplate={pkg => setParam("preview", String(pkg.id))}
          onViewAll={() => setParam("gallery", "1")}
          hideInspiration={!stripAllowed}
        />
      );
    }

    return (
      <>
        <div className={GRID_CLASS} data-testid="package-grid">
          {newTile}
          {packages.map(pkg => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              variant={cardVariantFor(pkg, isGlobalScope)}
              onClick={() => openPackage(pkg)}
            />
          ))}
          {grid.isFetchingNextPage &&
            Array.from({ length: FETCHING_ROW_COUNT }, (_, i) => (
              <PackageCard key={`next-${i}`} pkg={SKELETON_PACKAGE} variant="draft" onClick={() => {}} dataSkeleton />
            ))}
        </div>
        <div ref={sentinelRef} data-testid="package-grid-sentinel" aria-hidden="true" />
        {!grid.hasNextPage && grid.pagesLoaded > 1 && (
          <p className="text-center text-xs text-slate-400 py-4">You've reached the end</p>
        )}
      </>
    );
  };

  const body = (
    <>
      <div className="flex flex-col gap-6">
        {showStrip &&
          (strip.isLoading ? (
            <StripSkeleton />
          ) : (
            <InspirationStrip
              onPreview={pkg => setParam("preview", String(pkg.id))}
              onViewAll={() => setParam("gallery", "1")}
              onHide={() => setInspirationHidden(true)}
            />
          ))}
        {stripAllowed && inspirationHidden && !firstRun && (
          <div className="flex justify-end">
            <Button variant="link" size="sm" onClick={() => setInspirationHidden(false)}>
              Show inspiration
            </Button>
          </div>
        )}

        <PackageListToolbar
          q={filters.q}
          onQChange={q => setParam("q", q || null)}
          contextType={filters.type}
          onContextTypeChange={type => setParam("type", type)}
          status={filters.status}
          onStatusChange={status => setParam("status", status === "all" ? null : status)}
          adminScope={adminScope && { scope: adminScope.scope }}
          canEdit={canEdit}
        />

        {renderGrid()}
      </div>

      {/* Templates are a coach on-ramp; the admin views have no strip, so a stale ?preview or ?gallery is ignored. */}
      {!adminScope && (
        <>
          <TemplateGalleryDialog
            open={filters.gallery}
            onOpenChange={open => !open && setParam("gallery", null)}
            onPreview={pkg => setParam("preview", String(pkg.id))}
          />
          <TemplateQuickPreview
            pkg={previewTarget}
            open={filters.preview != null && previewTarget != null}
            onOpenChange={open => !open && setParam("preview", null)}
            onAdded={onAdded}
            onStartFromLook={pkg => history.push(URLS.coach.newDesign, { templateId: pkg.id })}
            canEdit={canEdit}
          />
        </>
      )}
      <DesignStudioHelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </>
  );

  const helpButton = (
    <Button variant="outline" onClick={() => setHelpOpen(true)}>
      How it works
    </Button>
  );
  // An admin in Coach scope must pick the coach a new design belongs to first.
  const newDesignButton = canEdit && !needsCoach ? <Button onClick={openNewDesign}>New design</Button> : null;

  return (
    <PageWrapper hideSeparator fitViewport className="flex min-h-0 flex-1 flex-col gap-0">
      {adminScope ? (
        <AdminDesignStudioHeader
          tab="designs"
          onScopeChange={onScopeChange}
          actions={
            <>
              {helpButton}
              {newDesignButton}
            </>
          }
        />
      ) : (
        <CoachDesignStudioHeader
          tab="designs"
          actions={
            <>
              {helpButton}
              {newDesignButton}
            </>
          }
        />
      )}
      <div className="min-h-0 flex-1 overflow-y-auto p-4 md:p-6">{body}</div>
    </PageWrapper>
  );
}
